import React, { useState } from 'react';
import { 
  Megaphone, 
  CheckCircle, 
  XCircle, 
  Clock,
  Calendar,
  Plus,
  Edit,
  Trash2
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import AdminAnnouncementModal from '../../components/admin/AdminAnnouncementModal';
import PillToggle from '../../components/shared/PillToggle';
import { useAdminAnnouncements } from '../../hooks/useAdminAnnouncements';
import { formatDate } from '../../utils/dateUtils';

const AdminAnnouncements = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const {
    announcements,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    announcementStatusOptions,
    targetAudienceOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    getAnnouncementStatus,
    toggleStatus,
    isToggling,
    deleteAnnouncement
  } = useAdminAnnouncements();

  // Define table columns
  const columns = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (announcement) => (
        <p className="text-sm font-medium text-gray-900">
          {announcement.title || 'N/A'}
        </p>
      )
    },
    {
      key: 'announcement_type',
      label: 'Type',
      sortable: true,
      width: 'w-32',
      render: (announcement) => (
        <span className="text-sm font-medium text-gray-900 capitalize">
          {announcement.announcement_type || 'General'}
        </span>
      )
    },
    {
      key: 'target_audience',
      label: 'Audience',
      sortable: true,
      width: 'w-32',
      render: (announcement) => (
        <span className="text-sm font-medium text-gray-900 capitalize">
          {announcement.target_audience || 'All'}
        </span>
      )
    },
    {
      key: 'dates',
      label: 'Schedule',
      sortable: false,
      width: 'w-48',
      render: (announcement) => (
        <div className="text-xs text-gray-600">
          {announcement.start_date && (
            <div>
              <span className="font-medium">Start:</span> {formatDate(announcement.start_date, 'short')}
            </div>
          )}
          {announcement.end_date && (
            <div>
              <span className="font-medium">End:</span> {formatDate(announcement.end_date, 'short')}
            </div>
          )}
          {!announcement.start_date && !announcement.end_date && (
            <span className="text-gray-400">No schedule</span>
          )}
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (announcement) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(announcement.created_at, 'medium')}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (announcement) => {
        const status = getAnnouncementStatus(announcement);
        const statusConfig = {
          active: { color: 'bg-green-100 text-green-800', label: 'Active' },
          upcoming: { color: 'bg-blue-100 text-blue-800', label: 'Upcoming' },
          expired: { color: 'bg-gray-100 text-gray-800', label: 'Expired' },
          inactive: { color: 'bg-red-100 text-red-800', label: 'Inactive' }
        };
        const config = statusConfig[status] || statusConfig.inactive;
        
        return (
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
              {config.label}
            </span>
            <PillToggle
              checked={Boolean(announcement.is_active)}
              onChange={() => toggleStatus(announcement.id)}
              loading={isToggling}
              variant={announcement.is_active ? 'success' : 'amber'}
              onLabel="On"
              offLabel="Off"
              ariaLabel={`Toggle announcement ${announcement.title || announcement.id}`}
            />
          </div>
        );
      }
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (announcement) => {
        setEditingAnnouncement(announcement);
        setShowCreateModal(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (announcement) => {
        if (window.confirm('Are you sure you want to delete this announcement?')) {
          deleteAnnouncement(announcement.id);
        }
      }
    }
  ];

  const { totalAnnouncements, activeAnnouncements, upcomingAnnouncements } = statistics;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Announcements</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load announcements. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Megaphone className="w-6 h-6" />
                Announcements
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage platform-wide announcements and notifications
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingAnnouncement(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Announcement
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Announcements</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalAnnouncements}</p>
              </div>
              <Megaphone className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{activeAnnouncements}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{upcomingAnnouncements}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <SearchSelect
              label="Active Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              options={activeStatusOptions}
            />
            <SearchSelect
              label="Announcement Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              options={announcementStatusOptions}
            />
            <SearchSelect
              label="Target Audience"
              value={filters.target_audience}
              onChange={(value) => handleFilterChange('target_audience', value)}
              options={targetAudienceOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={announcements}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search announcements by title, content, or type..."
            onSearch={handleSearch}
            emptyMessage="No announcements found"
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      <AdminAnnouncementModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingAnnouncement(null);
        }}
        announcement={editingAnnouncement}
      />
    </div>
  );
};

export default AdminAnnouncements;

